import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { DBNotification } from "../types";
import { HISTORY_KEY, INBOX_KEY } from "../queryKeys";

/**
 * Accept / decline a workspace invitation delivered as a
 * `workspace_invite` notification.
 *
 * SECURITY: accept goes through the `accept-invite` Edge Function, NOT
 * a direct DB write. The Edge Function validates the invite (expiry,
 * ownership, existing membership) server-side before upserting the
 * membership row. The client never writes directly to
 * `workspace_members` — keeps RLS surface minimal.
 *
 * Decline is just an archive: the invite row itself is left alone in
 * case the user changes their mind (the original link still works until
 * the invite expires).
 */
export function useWorkspaceInviteActions() {
  const queryClient = useQueryClient();

  const acceptInvite = useMutation({
    mutationFn: async (notif: DBNotification) => {
      const url         = new URL(notif.action_url ?? "", "https://app");
      const workspaceId = url.searchParams.get("invite");
      if (!workspaceId) throw new Error("Invalid invite link.");

      const { data: { session }, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError || !session?.user) throw new Error("No active session.");

      // ── Secure Edge Function call — no direct DB write ──────────
      const { error: fnError } = await supabase.functions.invoke(
        "accept-invite",
        { body: { workspaceId } },
      );
      if (fnError) throw fnError;

      // Archive the notification after the Edge Function succeeds
      const { error: archiveError } = await supabase
        .from("notifications")
        .update({ is_read: true, is_archived: true })
        .eq("id", notif.id);
      if (archiveError) throw archiveError;
    },
    onMutate: async (notif) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.filter((n) => n.id !== notif.id),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      // Success is silent: row leaves inbox; workspace UI updates. No toast.
    },
    onError: (err: Error, _notif, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({
        title:       "Failed to accept invite",
        description: err.message,
        variant:     "destructive",
      });
    },
  });

  const declineInvite = useMutation({
    mutationFn: async (notif: DBNotification) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true, is_archived: true })
        .eq("id", notif.id);
      if (error) throw error;
    },
    onMutate: async (notif) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.filter((n) => n.id !== notif.id),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    },
    onError: (err: Error, _notif, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({
        title:       "Failed to decline invite",
        description: err.message,
        variant:     "destructive",
      });
    },
  });

  return { acceptInvite, declineInvite };
}
